import { useState } from "react"
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router"
import { toast } from "sonner"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button, buttonVariants } from "@/components/ui/button"
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field"
import { Input } from "@/components/ui/input"
import { Spinner } from "@/components/ui/spinner"
import { authClient } from "@/lib/auth-client"

export const Route = createFileRoute("/app/change-password")({
  validateSearch: (
    search: Record<string, unknown>
  ): { token?: string; error?: string } => ({
    token:
      typeof search.token === "string" && search.token.length <= 256
        ? search.token
        : undefined,
    error: typeof search.error === "string" ? "INVALID_TOKEN" : undefined,
  }),
  head: () => ({
    meta: [
      { title: "Change password | intoshort" },
      { name: "robots", content: "noindex,nofollow" },
      { name: "referrer", content: "no-referrer" },
    ],
  }),
  component: ChangePasswordPage,
})

function ChangePasswordPage() {
  const { token, error: linkError } = Route.useSearch()
  const { data: session } = authClient.useSession()
  const navigate = useNavigate()
  const [password, setPassword] = useState("")
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [invalidLink, setInvalidLink] = useState(false)

  async function save(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (pending || !token || !session) return
    setPending(true)
    setError(null)
    try {
      const result = await authClient.resetPassword({
        token,
        newPassword: password,
      })
      if (result.error) {
        if (result.error.code === "INVALID_TOKEN") setInvalidLink(true)
        setError(
          result.error.status === 401
            ? "Sign in again, then request a new link from Account."
            : "We could not change your password. The link may have expired or already been used. Request a new link from Account."
        )
        return
      }
      setPassword("")
      try {
        sessionStorage.setItem(`password-updated:${session.user.id}`, "true")
      } catch {
        /* The toast still confirms success. */
      }
      toast.success("Password updated.")
      await navigate({ to: "/app/account", replace: true })
    } catch {
      setError("Unable to reach the server. Please try again.")
    } finally {
      setPending(false)
    }
  }

  return (
    <main className="app-main" aria-labelledby="change-password-title">
      <section className="flex w-full max-w-lg flex-col gap-8">
        <h1 id="change-password-title" className="text-3xl font-semibold">
          Change password
        </h1>
        {!token || linkError || invalidLink ? (
          <Alert variant="destructive">
            <AlertDescription>
              This password change link is invalid or has expired. Request a new
              link from Account.
            </AlertDescription>
          </Alert>
        ) : (
          <form onSubmit={save} aria-busy={pending}>
            <FieldGroup>
              <Field data-disabled={pending}>
                <FieldLabel htmlFor="new-password">New password</FieldLabel>
                <Input
                  id="new-password"
                  type="password"
                  autoComplete="new-password"
                  required
                  minLength={8}
                  maxLength={128}
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  disabled={pending}
                  aria-describedby="new-password-help"
                />
                <FieldDescription id="new-password-help">
                  Use 8 to 128 characters. Your other sessions will be signed
                  out.
                </FieldDescription>
              </Field>
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              <Button type="submit" className="self-start" disabled={pending}>
                {pending && <Spinner data-icon="inline-start" />}
                {pending ? "Saving…" : "Save"}
              </Button>
            </FieldGroup>
          </form>
        )}
        <Link
          to="/app/account"
          replace
          className={buttonVariants({
            variant: "ghost",
            className: "self-start",
          })}
        >
          Back to Account
        </Link>
      </section>
    </main>
  )
}
